/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/standard-button';
import { cssMap, cx } from '@atlaskit/css';
import EmptyState from '@atlaskit/empty-state';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const unboundedStyles = unboundedCssMap({
	container: {
		overflow: 'hidden',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: token('elevation.shadow.raised'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.input'),
	},
	viewMode: {
		'&:hover': {
			boxShadow: `0px 5px 12px 0px ${token('color.border')}`,
			cursor: 'pointer',
		},
	},
});

const styles = cssMap({
	subheading: {
		font: token('font.body'),
		color: token('color.text.subtlest'),
		paddingInline: token('space.300'),
		marginBottom: token('space.200'),
	},
});

type ErrorStateProps = {
	description: Record<string, React.ReactNode>;
	heading: Record<string, React.ReactNode>;
	image: string;
	isInViewMode: boolean;
	itemHeight: number;
	itemId: string;
	link?: string;
	primaryAction?: Record<string, React.ReactNode>;
};

export const ErrorState = ({
	itemId,
	heading,
	description,
	primaryAction,
	image,
	itemHeight,
	link,
	isInViewMode,
}: ErrorStateProps) => {
	const { formatMessage } = useIntl();

	const ErrorContent = () => (
		<EmptyState
			header={formatMessage(heading)}
			description={
				<Box xcss={cx(styles.subheading)}>
					<FormattedMessage {...description} />
				</Box>
			}
			primaryAction={
				!!primaryAction && (
					<Button href={link} target="_blank">
						<FormattedMessage {...primaryAction} />
					</Button>
				)
			}
			renderImage={() => <Image src={image} alt={formatMessage(heading)} />}
		/>
	);

	return (
		<div
			id={itemId}
			css={[unboundedStyles.container, isInViewMode && unboundedStyles.viewMode]}
			style={{ height: itemHeight }}
		>
			<ErrorContent />
		</div>
	);
};
